import { Box, IconButton, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import {
  BUILDER_STEP_CONTROLS_BUTTONS,
  BUILDER_VIEW_TYPE_TABS,
  UNDO,
  VIEW_TYPE_ICONS,
} from '../../../../../constants/templates';
import { ClaimLinkEditorViewTypeEnum } from '../../../../../types/templates';
import { ZoomController } from '../../../_components_';

interface IProps {
  viewType: ClaimLinkEditorViewTypeEnum;
  onViewTypeChange: (viewType: ClaimLinkEditorViewTypeEnum) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

export const EditorToolbar = ({ viewType, onViewTypeChange, zoom, onZoomChange, onUndo, onRedo }: IProps) => {
  const theme = useTheme();
  const accentColor = theme.wl?.portal_accent_color || theme.palette.primary.main;

  const handleViewTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newViewType: ClaimLinkEditorViewTypeEnum | null) => {
      if (newViewType) {
        onViewTypeChange(newViewType);
      }
    },
    [onViewTypeChange],
  );

  const handleZoomChange = useCallback(
    (newZoom: number) => {
      onZoomChange(newZoom);
    },
    [onZoomChange],
  );

  const iconButtonStyles = { p: 0.7 };

  const deviceTypeButtonsStyles = useMemo(
    () => ({
      gap: 1,
      '& .MuiToggleButtonGroup-grouped': { borderRadius: 0.5 },
      '& .MuiToggleButton-root': {
        border: 'none',
        textTransform: 'none',
        px: 1,
        py: 0.5,
        fontSize: 13,
        color: theme.palette.action.active,
      },
      '& .MuiToggleButton-root.Mui-selected': {
        border: 'none',
        color: accentColor,
      },
    }),
    [theme, accentColor],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {BUILDER_STEP_CONTROLS_BUTTONS.map((button) => {
          const { icon: Icon, label, dataAction } = button;
          const isUndo = label === UNDO;
          const handler = isUndo ? onUndo : onRedo;
          return (
            <IconButton
              key={label}
              sx={iconButtonStyles}
              data-action={dataAction}
              onClick={handler}
              disabled={!handler}
              aria-label={label}
            >
              <Icon />
            </IconButton>
          );
        })}
      </Box>
      <ToggleButtonGroup
        color="primary"
        value={viewType}
        exclusive
        onChange={handleViewTypeChange}
        aria-label="Platform"
        sx={deviceTypeButtonsStyles}
      >
        {BUILDER_VIEW_TYPE_TABS.map((tab) => {
          const { value, label, dataAction } = tab;
          const Icon = VIEW_TYPE_ICONS[value];
          return (
            <ToggleButton key={value} value={value} data-action={dataAction} sx={{ display: 'flex', gap: 0.5 }}>
              <Icon width={16} height={16} />
              {label}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      <ZoomController value={zoom} onChange={handleZoomChange} dataAction="template_builder" />
    </Box>
  );
};
